import type { ComponentProps } from "react"
import { Box, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import type { MarkdownToJSX } from "markdown-to-jsx"
import { formatArtifactSize } from "helpers/formatters/file-size"
import { ChatMarkdown, sanitizeMarkdownUrl } from "./chat-markdown"
import { MAX_ENTRY_PREVIEW_BYTES, type SkillPackageEntry } from "./skill-package-contents"
import { isMarkdownPath, languageLabel } from "./skill-package-paths"

const ROOT_SX = { flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column", gap: 1 } satisfies SxProps<Theme>
const CAPTION_ROW_SX = { display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", minWidth: 0 } satisfies SxProps<Theme>
const PATH_SX = { fontFamily: "monospace", fontSize: "0.75rem", color: "text.secondary", overflowWrap: "anywhere" } satisfies SxProps<Theme>
const CHIP_SX = { flexShrink: 0, fontSize: "0.6875rem", color: "text.secondary", border: 1, borderColor: "divider", borderRadius: 1, px: 0.75, py: 0.125 } satisfies SxProps<Theme>
const PROSE_SX = { minWidth: 0, "& > :first-child": { mt: 0 }, "& > :last-child": { mb: 0 } } satisfies SxProps<Theme>
const CODE_BLOCK_SX = { minWidth: 0, overflow: "auto", backgroundColor: "var(--app-bg-muted)", border: 1, borderColor: "divider", borderRadius: 1, p: 1.5 } satisfies SxProps<Theme>
const CODE_SX = { m: 0, fontFamily: "monospace", fontSize: "0.8125rem", lineHeight: 1.6, whiteSpace: "pre" } satisfies SxProps<Theme>

function SkillPackageLink(props: ComponentProps<"a">) {
  return <a {...props} target="_blank" rel="noopener noreferrer nofollow" />
}

const MARKDOWN_OPTIONS: MarkdownToJSX.Options = {
  disableParsingRawHTML: true,
  sanitizer: sanitizeMarkdownUrl,
  overrides: { a: { component: SkillPackageLink } },
}

function withheldMessage(entry: SkillPackageEntry): string {
  if (entry.body.kind === "binary") return "This file isn't text, so it can't be shown here. Add the skill to download it."
  if (entry.body.kind === "tooLarge") return `This file is too large to show here (over ${formatArtifactSize(MAX_ENTRY_PREVIEW_BYTES)}).`
  return "This file wasn't read: the package is larger than the preview budget."
}

function FileBody({ entry }: { entry: SkillPackageEntry }) {
  if (entry.body.kind !== "text") {
    return (
      <Typography variant="body2" color="text.secondary" data-testid="kelly-skill-package-withheld">
        {withheldMessage(entry)}
      </Typography>
    )
  }
  if (isMarkdownPath(entry.path)) {
    return (
      <Box sx={PROSE_SX} data-testid="kelly-skill-package-markdown">
        <ChatMarkdown options={MARKDOWN_OPTIONS}>{entry.body.text}</ChatMarkdown>
      </Box>
    )
  }
  return (
    <Box sx={CODE_BLOCK_SX} data-testid="kelly-skill-package-code">
      <Typography component="pre" sx={CODE_SX}>{entry.body.text}</Typography>
    </Box>
  )
}

export function SkillPackageFileView({ entry }: { entry: SkillPackageEntry }) {
  const language = languageLabel(entry.path)
  return (
    <Box sx={ROOT_SX} data-testid="kelly-skill-package-view" data-entry-path={entry.path}>
      <Box sx={CAPTION_ROW_SX}>
        <Typography component="span" sx={PATH_SX} data-testid="kelly-skill-package-view-path">{entry.path}</Typography>
        {language !== null && (
          <Typography component="span" sx={CHIP_SX} data-testid="kelly-skill-package-language">{language}</Typography>
        )}
      </Box>
      <FileBody entry={entry} />
    </Box>
  )
}
